import React, { FC, SyntheticEvent } from "react";
import {
    Badge,
    Button,
    Col,
    ListGroup,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import {
    BatchBuild,
    BatchBuildState,
    BuildType,
    CancelBatchBuildData,
    CancelBatchBuildVariables,
    ReleaseType
} from "../../models/types";
import { getFormattedTime } from "../../common/Helpers";
import { useMutation } from "@apollo/client";
import {
    CANCEL_BUILD,
    UPDATE_BUILD_DEVELOPMENT
} from "../../graphql/mutations";
import { GET_RESULTS } from "../../graphql/queries";
import { StepProgressBar } from "./pipeline/StepProgressBar";
import {
    faEye,
    faEyeSlash,
    faPlay,
    faUser
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import cx from "classnames";
import styles from "./HistoryItem.module.scss";

interface Props {
    developerMode: boolean;
    revisionType: ReleaseType;
    object: BatchBuild;
    linkPath: string;
    isLatestBuild: boolean;
    isClickable: boolean;
    openErrorLogs: (buildId: number) => void;
    updateBuildState: (buildId: number, newState: BatchBuildState) => void;
}

const getBadgeVariation = (type: any) => {
    switch (type) {
        case BatchBuildState.Started:
        case BatchBuildState.JobCreated:
        case BatchBuildState.JobStarted:
        case BatchBuildState.JobBuilt:
        case BatchBuildState.JobCleaned:
            return "info";
        case BatchBuildState.Completed:
            return "primary";
        case BatchBuildState.Review:
            return "review";
        case BatchBuildState.Failed:
            return "danger";
        case BatchBuildState.Test:
            return "test";
        case BatchBuildState.Stopped:
            return "mist";
        case "warning":
            return "warning";
        case "latest":
            return "success";
        default:
            console.log("[DEBUG] Unknown type ", type);
            break;
    }
};

export const HistoryItem: FC<Props> = ({
    linkPath,
    object,
    developerMode,
    revisionType,
    updateBuildState,
    openErrorLogs,
    isLatestBuild,
    isClickable
}) => {
    const navigate = useNavigate();
    const buildInfo = object.name ? object.name : `Build ${object.id}`;
    const buildInfoTooltip = object.name
        ? object.name
        : `No name - build number ${object.id}`;
    const formattedDate = ` ${getFormattedTime(object.dateCreated)}`;

    const [cancelBuild] = useMutation<
        CancelBatchBuildData,
        CancelBatchBuildVariables
    >(CANCEL_BUILD);

    const [updateBuildDevelopment] = useMutation(UPDATE_BUILD_DEVELOPMENT);

    const toggleDevelopmentBuild =
        (value: boolean) => (event: SyntheticEvent) => {
            event.preventDefault();
            event.stopPropagation();
            updateBuildDevelopment({
                variables: { id: object.id, developmentBuild: value },
                refetchQueries: [
                    {
                        query: GET_RESULTS,
                        variables: { revisionId: object.revision.id }
                    }
                ]
            });
        };

    const startCancelJob = async () => {
        try {
            await cancelBuild({
                variables: { buildId: Number(object.id) },
                refetchQueries: [
                    {
                        query: GET_RESULTS,
                        variables: {
                            revisionId: object.revision.id,
                            clientTest:
                                !developerMode &&
                                revisionType === ReleaseType.Development
                        }
                    }
                ]
            });
        } catch (error) {
            console.log("[DEBUG] startCancelJob error ", error);
        }
    };

    return (
        <ListGroup.Item
            className={cx("mx-2 my-1 bread-text", styles.item, {
                [styles.clickable]: isClickable,
                [styles.muted]: !isClickable
            })}
            onClick={() => {
                if (isClickable) {
                    navigate(linkPath);
                } else if (developerMode) {
                    openErrorLogs(Number(object.id));
                }
            }}
        >
            <Row className="d-flex align-items-center g-0">
                <Col className="col-4 overflow-hidden">
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 550, hide: 300 }}
                        overlay={
                            <Tooltip id="build-history-name-tooltip">
                                {buildInfoTooltip}
                            </Tooltip>
                        }
                    >
                        <span className="bread-text d-block text-truncate">
                            {buildInfo}
                        </span>
                    </OverlayTrigger>
                </Col>
                <Col className="col-2">
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 550, hide: 300 }}
                        overlay={
                            <Tooltip id="build-history-date-tooltip">
                                Start time
                            </Tooltip>
                        }
                    >
                        <span className="analytics-font">{formattedDate}</span>
                    </OverlayTrigger>
                </Col>
                <Col className="col-1 d-flex justify-content-center">
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 550, hide: 300 }}
                        overlay={
                            <Tooltip id="build-history-user-tooltip">
                                {object.user?.displayName ?? "unknown"}
                            </Tooltip>
                        }
                    >
                        {object.user?.pictureUrl ? (
                            <div>
                                <img
                                    src={object.user?.pictureUrl}
                                    className={styles.userPicture}
                                    alt="User"
                                />
                            </div>
                        ) : (
                            <div className={styles.userIcon}>
                                <FontAwesomeIcon icon={faUser} />
                            </div>
                        )}
                    </OverlayTrigger>
                </Col>
                <Col className="text-start ps-2 col-auto">
                    {developerMode && object.buildType === BuildType.Mutator ? (
                        <Badge bg="mutator" className="ms-2">
                            Mutator
                        </Badge>
                    ) : null}
                    {object.state !== BatchBuildState.Completed && (
                        <Badge
                            className="ms-2"
                            bg={getBadgeVariation(object.state)}
                        >
                            {BatchBuildState[object.state]}
                        </Badge>
                    )}
                    {object.developmentBuild && (
                        <Badge className="ms-2" bg="info">
                            Dev
                        </Badge>
                    )}
                    {isLatestBuild && developerMode ? (
                        <Badge
                            bg={getBadgeVariation("latest")}
                            className="ms-2"
                        >
                            Latest
                        </Badge>
                    ) : null}
                    {object.state < BatchBuildState.Completed ||
                    (object.state === BatchBuildState.Failed &&
                        developerMode) ? (
                        <StepProgressBar build={object} />
                    ) : null}
                </Col>
                {!developerMode && (
                    <Col className="col d-flex px-0 justify-content-end align-items-center">
                        {object.state >= BatchBuildState.Completed &&
                            object.state !== BatchBuildState.Failed &&
                            object.state !== BatchBuildState.Stopped && (
                                <OverlayTrigger
                                    placement="top"
                                    delay={{ show: 550, hide: 300 }}
                                    overlay={
                                        <Tooltip id="build-history-item-tooltip">
                                            Go to build report / ads
                                        </Tooltip>
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={faPlay}
                                        className="pe-2 text-primary"
                                    />
                                </OverlayTrigger>
                            )}
                    </Col>
                )}
                {developerMode && object.buildType !== BuildType.Mutator && (
                    <Col className="col d-flex px-0 justify-content-end align-items-center">
                        {!object.developmentBuild ? (
                            <FontAwesomeIcon
                                onClick={toggleDevelopmentBuild(true)}
                                className="mx-2 text-info mouseHover"
                                icon={faEye}
                                size="lg"
                                title="Mark as developer only"
                            />
                        ) : (
                            <FontAwesomeIcon
                                className="mx-2 text-secondary mouseHover"
                                icon={faEyeSlash}
                                size="lg"
                                onClick={toggleDevelopmentBuild(false)}
                            />
                        )}
                        {object.state <= BatchBuildState.JobCleaned &&
                        object.jobIds &&
                        object.jobIds.length > 0 ? (
                            <Button
                                onClick={(event: SyntheticEvent) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    startCancelJob();
                                }}
                                variant="danger"
                                className="btn-sm ms-1"
                            >
                                Cancel
                            </Button>
                        ) : null}
                    </Col>
                )}
                {developerMode && object.buildType === BuildType.Mutator && (
                    <Col className="col-md-4 col-lg-3"></Col>
                )}
            </Row>
        </ListGroup.Item>
    );
};
